import { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Underline as UnderlineIcon,
  List, ListOrdered, Link2, Sparkles, Type, Highlighter,
} from 'lucide-react';
import AiReplyAssistModal from './AiReplyAssistModal';

// ── FontSize custom extension (same pattern as EmailEditor) ──────────────────
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el: HTMLElement) => el.style.fontSize || null,
          renderHTML: (attrs: Record<string, unknown>) =>
            attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFontSize: (size: string) => ({ chain }: any) =>
        chain().setMark('textStyle', { fontSize: size }).run(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      unsetFontSize: () => ({ chain }: any) =>
        chain().setMark('textStyle', { fontSize: null }).run(),
    };
  },
});

const FONT_SIZES = ['12px', '13px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

const TEXT_COLORS = [
  '#000000', '#374151', '#6b7280', '#9ca3af',
  '#dc2626', '#ea580c', '#d97706', '#16a34a',
  '#0284c7', '#7c3aed', '#db2777', '#0f766e',
];

const HIGHLIGHT_COLORS = [
  '#fef08a', '#bbf7d0', '#bfdbfe', '#fecdd3',
  '#fed7aa', '#e9d5ff', '#99f6e4', '#fde68a',
  'transparent',
];

// ── Color picker popover ─────────────────────────────────────────────────────
interface ColorPickerProps {
  colors: string[];
  onSelect: (color: string) => void;
  onClose: () => void;
}

function ColorPicker({ colors, onSelect, onClose }: ColorPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={ref}
      className="rre-color-picker"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="rre-color-grid">
        {colors.map((c) => (
          <button
            key={c}
            type="button"
            className="rre-color-swatch"
            style={{
              background: c === 'transparent'
                ? 'linear-gradient(135deg, #fff 45%, #f87171 45%, #f87171 55%, #fff 55%)'
                : c,
              border: c === 'transparent' ? '1px solid #e5e7eb' : '1px solid rgba(0,0,0,0.12)',
            }}
            title={c === 'transparent' ? 'Remove highlight' : c}
            onClick={() => { onSelect(c); onClose(); }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Public handle type ───────────────────────────────────────────────────────
export interface ReplyRichEditorHandle {
  setContent: (html: string) => void;
  getHTML: () => string;
  clearContent: () => void;
  focus: () => void;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface ReplyRichEditorProps {
  placeholder?: string;
  onChange?: (html: string, isEmpty: boolean) => void;
  leadContext?: { name: string; lastMessage: string };
}

// ── Component ─────────────────────────────────────────────────────────────────
const ReplyRichEditor = forwardRef<ReplyRichEditorHandle, ReplyRichEditorProps>(
  function ReplyRichEditor({ placeholder, onChange, leadContext }, ref) {
    const [showAi, setShowAi] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHighlightPicker, setShowHighlightPicker] = useState(false);

    const editor = useEditor({
      extensions: [
        StarterKit,
        Underline,
        TextStyle,
        FontSize,
        Color,
        Highlight.configure({ multicolor: true }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        Placeholder.configure({ placeholder: placeholder ?? 'Write your reply here…' }),
      ],
      content: '',
      onUpdate: ({ editor }) => {
        onChange?.(editor.getHTML(), editor.isEmpty);
      },
    });

    useImperativeHandle(ref, () => ({
      setContent: (html: string) => {
        editor?.chain().focus().setContent(html).run();
      },
      getHTML: () => editor?.getHTML() ?? '',
      clearContent: () => {
        editor?.chain().focus().clearContent().run();
      },
      focus: () => {
        editor?.commands.focus();
      },
    }));

    const handleLinkToggle = () => {
      if (!editor) return;
      if (editor.isActive('link')) {
        editor.chain().focus().unsetLink().run();
      } else {
        const url = window.prompt('Enter URL', 'https://');
        if (url) {
          editor.chain().focus().setLink({ href: url.trim() }).run();
        }
      }
    };

    const currentFontSize = editor?.getAttributes('textStyle')?.fontSize ?? '';

    return (
      <div className="rre-wrap">
        <EditorContent editor={editor} className="rre-content" />

        {/* ── Toolbar ── */}
        <div className="rre-toolbar">
          {/* Bold */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('bold') ? ' rre-btn--active' : ''}`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            title="Bold"
          ><Bold size={13} /></button>

          {/* Italic */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('italic') ? ' rre-btn--active' : ''}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            title="Italic"
          ><Italic size={13} /></button>

          {/* Underline */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('underline') ? ' rre-btn--active' : ''}`}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            title="Underline"
          ><UnderlineIcon size={13} /></button>

          <span className="rre-divider" />

          {/* Font size */}
          <select
            className="rre-select"
            value={currentFontSize}
            onChange={(e) => {
              if (!editor) return;
              if (e.target.value) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).setFontSize(e.target.value).run();
              } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).unsetFontSize().run();
              }
            }}
            title="Font Size"
          >
            <option value="">Size</option>
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>{s.replace('px', '')}</option>
            ))}
          </select>

          {/* Text color */}
          <div className="rre-color-wrap">
            <button
              type="button"
              className={`rre-btn rre-btn--color${showColorPicker ? ' rre-btn--active' : ''}`}
              onClick={() => { setShowColorPicker((v) => !v); setShowHighlightPicker(false); }}
              title="Text Color"
            >
              <Type size={13} />
              <span
                className="rre-color-bar"
                style={{ background: editor?.getAttributes('textStyle')?.color || '#000' }}
              />
            </button>
            {showColorPicker && (
              <ColorPicker
                colors={TEXT_COLORS}
                onSelect={(color) => { editor?.chain().focus().setColor(color).run(); }}
                onClose={() => setShowColorPicker(false)}
              />
            )}
          </div>

          {/* Highlight */}
          <div className="rre-color-wrap">
            <button
              type="button"
              className={`rre-btn rre-btn--color${showHighlightPicker ? ' rre-btn--active' : ''}`}
              onClick={() => { setShowHighlightPicker((v) => !v); setShowColorPicker(false); }}
              title="Highlight"
            >
              <Highlighter size={13} />
              <span
                className="rre-color-bar"
                style={{
                  background:
                    (editor?.isActive('highlight') && editor.getAttributes('highlight')?.color) ||
                    '#fef08a',
                }}
              />
            </button>
            {showHighlightPicker && (
              <ColorPicker
                colors={HIGHLIGHT_COLORS}
                onSelect={(color) => {
                  if (color === 'transparent') {
                    editor?.chain().focus().unsetHighlight().run();
                  } else {
                    editor?.chain().focus().setHighlight({ color }).run();
                  }
                }}
                onClose={() => setShowHighlightPicker(false)}
              />
            )}
          </div>

          <span className="rre-divider" />

          {/* Bullet list */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('bulletList') ? ' rre-btn--active' : ''}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            title="Bullet List"
          ><List size={13} /></button>

          {/* Ordered list */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('orderedList') ? ' rre-btn--active' : ''}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            title="Ordered List"
          ><ListOrdered size={13} /></button>

          <span className="rre-divider" />

          {/* Link */}
          <button
            type="button"
            className={`rre-btn${editor?.isActive('link') ? ' rre-btn--active' : ''}`}
            onClick={handleLinkToggle}
            title="Insert Link"
          ><Link2 size={13} /></button>

          <span className="rre-divider" />

          {/* AI Assist */}
          <button
            type="button"
            className="rre-btn-text rre-btn-text--ai"
            onClick={() => setShowAi(true)}
            title="AI Writing Assistant"
          >
            <Sparkles size={12} /> AI Assist
          </button>
        </div>

        {showAi && (
          <AiReplyAssistModal
            editor={editor}
            onClose={() => setShowAi(false)}
            leadName={leadContext?.name}
            lastMessage={leadContext?.lastMessage}
          />
        )}

        <style>{`
          .rre-wrap {
            border: 1px solid var(--gray-200, #e2e8f0);
            border-radius: 8px;
            overflow: visible;
            background: #fff;
          }
          .rre-content .ProseMirror {
            min-height: 120px;
            padding: 12px 14px;
            outline: none;
            font-size: 13px;
            line-height: 1.6;
            color: var(--gray-700, #374151);
          }
          .rre-content .ProseMirror p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            color: var(--gray-400, #9ca3af);
            pointer-events: none;
            float: left;
            height: 0;
          }
          .rre-content .ProseMirror ul  { padding-left: 20px; list-style-type: disc; }
          .rre-content .ProseMirror ol  { padding-left: 20px; list-style-type: decimal; }
          .rre-content .ProseMirror a   { color: #0284c7; text-decoration: underline; }
          .rre-content .ProseMirror strong { font-weight: 600; }
          .rre-content .ProseMirror em  { font-style: italic; }
          .rre-content .ProseMirror u   { text-decoration: underline; }
          .rre-content .ProseMirror mark { border-radius: 2px; padding: 0 2px; }
          .rre-content .ProseMirror p   { margin: 0 0 0.4em; }
          .rre-content .ProseMirror p:last-child { margin-bottom: 0; }

          .rre-toolbar {
            display: flex;
            align-items: center;
            gap: 1px;
            padding: 5px 8px;
            border-top: 1px solid #f1f5f9;
            background: #f8fafc;
            flex-wrap: wrap;
          }
          .rre-divider {
            display: inline-block;
            width: 1px;
            height: 16px;
            background: #e2e8f0;
            margin: 0 3px;
            flex-shrink: 0;
          }
          .rre-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border-radius: 5px;
            border: none;
            background: transparent;
            cursor: pointer;
            color: #64748b;
            transition: background 0.12s, color 0.12s;
            flex-shrink: 0;
          }
          .rre-btn:hover { background: #e2e8f0; color: #1e293b; }
          .rre-btn--active { background: #dbeafe; color: #1d4ed8; }
          .rre-btn--active:hover { background: #bfdbfe; }
          .rre-btn--color {
            flex-direction: column;
            gap: 0;
            width: 26px;
            height: 26px;
            padding: 2px 4px 2px;
          }
          .rre-color-bar {
            display: block;
            width: 14px;
            height: 3px;
            border-radius: 2px;
            margin-top: 1px;
            flex-shrink: 0;
          }
          .rre-select {
            height: 26px;
            padding: 0 4px;
            font-size: 12px;
            border: 1px solid #e2e8f0;
            border-radius: 5px;
            background: #fff;
            color: #374151;
            cursor: pointer;
            outline: none;
            min-width: 52px;
          }
          .rre-select:focus { border-color: #93c5fd; }
          .rre-btn-text {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 0 8px;
            height: 26px;
            border-radius: 5px;
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
            color: #64748b;
            transition: background 0.12s;
            white-space: nowrap;
            flex-shrink: 0;
          }
          .rre-btn-text:hover { background: #e2e8f0; color: #1e293b; }
          .rre-btn-text--ai { color: #1e3a5f; background: #e0f2fe; }
          .rre-btn-text--ai:hover { background: #bae6fd; color: #0c4a6e; }
          .rre-color-wrap { position: relative; display: inline-flex; }
          .rre-color-picker {
            position: absolute;
            bottom: calc(100% + 6px);
            left: 0;
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 10px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.12);
            z-index: 100;
            width: 148px;
          }
          .rre-color-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 4px;
          }
          .rre-color-swatch {
            width: 28px;
            height: 20px;
            border-radius: 4px;
            cursor: pointer;
            transition: transform 0.1s;
          }
          .rre-color-swatch:hover { transform: scale(1.12); }
        `}</style>
      </div>
    );
  }
);

export default ReplyRichEditor;
